const CHECKOUT_PENDING_KEY = 'pp_checkout_pending_v1';
const PURCHASE_TRACKED_PREFIX = 'pp_purchase_tracked_v1:';
const MAX_PENDING_AGE_MS = 12 * 60 * 60 * 1000;

export function markCheckoutPending(metadata = {}) {
  try {
    const orderId = typeof metadata.orderId === 'string' ? metadata.orderId.trim() : '';
    const subtotalCents = Number(metadata.subtotalCents);
    const itemCount = Number(metadata.itemCount);
    const legalVersion = typeof metadata.legalVersion === 'string' ? metadata.legalVersion.trim() : '';

    const serialized = JSON.stringify({
      startedAt: Date.now(),
      orderId: orderId || undefined,
      subtotalCents: Number.isFinite(subtotalCents) ? Math.max(0, Math.round(subtotalCents)) : undefined,
      itemCount: Number.isFinite(itemCount) ? Math.max(0, Math.round(itemCount)) : undefined,
      legalVersion: legalVersion || undefined
    });

    // Save in both storages: session-first, local fallback after Stripe redirect.
    window.sessionStorage.setItem(CHECKOUT_PENDING_KEY, serialized);
    window.localStorage.setItem(CHECKOUT_PENDING_KEY, serialized);
  } catch {
    // no-op: sessionStorage might be unavailable in strict environments
  }
}

export function consumeCheckoutPending(expectedOrderId) {
  try {
    const sessionRaw = window.sessionStorage.getItem(CHECKOUT_PENDING_KEY);
    const localRaw = window.localStorage.getItem(CHECKOUT_PENDING_KEY);
    const raw = sessionRaw || localRaw;

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    const startedAt = Number(parsed?.startedAt);

    if (!Number.isFinite(startedAt) || startedAt <= 0) {
      return null;
    }

    if (Date.now() - startedAt > MAX_PENDING_AGE_MS) {
      return null;
    }

    const orderId = typeof parsed?.orderId === 'string' ? parsed.orderId.trim() : '';
    if (expectedOrderId && orderId !== expectedOrderId) return null;
    window.sessionStorage.removeItem(CHECKOUT_PENDING_KEY);
    window.localStorage.removeItem(CHECKOUT_PENDING_KEY);
    const subtotalCents = Number(parsed?.subtotalCents);
    const itemCount = Number(parsed?.itemCount);
    const legalVersion = typeof parsed?.legalVersion === 'string' ? parsed.legalVersion.trim() : '';

    return {
      startedAt,
      orderId: orderId || null,
      subtotalCents: Number.isFinite(subtotalCents) ? Math.max(0, Math.round(subtotalCents)) : null,
      itemCount: Number.isFinite(itemCount) ? Math.max(0, Math.round(itemCount)) : null,
      legalVersion: legalVersion || null
    };
  } catch {
    return null;
  }
}

export function clearCheckoutPending() {
  try {
    window.sessionStorage.removeItem(CHECKOUT_PENDING_KEY);
    window.localStorage.removeItem(CHECKOUT_PENDING_KEY);
  } catch {
    // ignore
  }
}

export function getCheckoutSessionIdFromLocation(search) {
  try {
    const rawSearch =
      typeof search === 'string'
        ? search
        : typeof window !== 'undefined'
          ? window.location.search
          : '';
    const params = new URLSearchParams(rawSearch);
    const sessionId = params.get('session_id');

    if (typeof sessionId !== 'string') {
      return null;
    }

    const normalized = sessionId.trim();
    return normalized || null;
  } catch {
    return null;
  }
}

function getPurchaseTrackedStorageKey(transactionId) {
  return `${PURCHASE_TRACKED_PREFIX}${transactionId}`;
}

export function hasTrackedPurchase(transactionId) {
  try {
    const normalized = typeof transactionId === 'string' ? transactionId.trim() : '';
    if (!normalized) {
      return false;
    }

    return window.localStorage.getItem(getPurchaseTrackedStorageKey(normalized)) === '1';
  } catch {
    return false;
  }
}

export function markPurchaseTracked(transactionId) {
  try {
    const normalized = typeof transactionId === 'string' ? transactionId.trim() : '';
    if (!normalized) {
      return;
    }

    window.localStorage.setItem(getPurchaseTrackedStorageKey(normalized), '1');
  } catch {
    // ignore
  }
}
